import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ShieldCheck, Save, CheckCircle2, AlertCircle, ArrowLeft, Clock, LayoutTemplate } from "lucide-react";
import { Switch } from "../components/ui/switch";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ISO -> value for <input type="datetime-local"> in local time
function isoToLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const off = d.getTimezoneOffset();
  const local = new Date(d.getTime() - off * 60000);
  return local.toISOString().slice(0, 16);
}

export default function AdminPage() {
  const [dateVal, setDateVal] = useState("");
  const [active, setActive] = useState(true);
  const [homeVariant, setHomeVariant] = useState("promo");
  const [password, setPassword] = useState("");
  const [status, setStatus] = useState(null); // {type, msg}
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/promo/countdown`)
      .then((res) => {
        setDateVal(isoToLocalInput(res.data?.end_date));
        setActive(res.data?.active ?? true);
      })
      .catch(() => {});
    axios
      .get(`${API}/site/config`)
      .then((res) => setHomeVariant(res.data?.home_variant === "classique" ? "classique" : "promo"))
      .catch(() => {});
  }, []);

  const save = async () => {
    setStatus(null);
    if (!password) {
      setStatus({ type: "error", msg: "Merci de saisir le mot de passe admin." });
      return;
    }
    setLoading(true);
    try {
      const end_date = dateVal ? new Date(dateVal).toISOString() : null;
      await axios.post(`${API}/promo/countdown`, { end_date, active, password });
      await axios.post(`${API}/site/config`, { home_variant: homeVariant, password });
      setStatus({
        type: "success",
        msg: "Enregistré ! La page d'accueil et le compte à rebours sont mis à jour pour tous les visiteurs.",
      });
    } catch (e) {
      const msg = e?.response?.status === 401 ? "Mot de passe incorrect." : "Une erreur est survenue. Réessaie.";
      setStatus({ type: "error", msg });
    } finally {
      setLoading(false);
    }
  };

  const VariantBtn = ({ value, label, desc }) => (
    <button
      type="button"
      onClick={() => setHomeVariant(value)}
      className={`flex-1 text-left rounded-xl border px-4 py-3 transition-all ${
        homeVariant === value
          ? "border-[#f6e3a8] bg-[#f6e3a8]/10"
          : "border-white/10 bg-[#0a1224] hover:border-white/25"
      }`}
    >
      <div className="flex items-center gap-2">
        <span
          className={`w-3.5 h-3.5 rounded-full border-2 ${
            homeVariant === value ? "border-[#f6e3a8] bg-[#f6e3a8]" : "border-white/40"
          }`}
        />
        <span className="text-white text-sm font-medium">{label}</span>
      </div>
      <p className="text-white/45 text-xs mt-1 pl-5">{desc}</p>
    </button>
  );

  return (
    <div className="min-h-screen ocean-bg stars flex items-center justify-center px-5 py-16">
      <div className="w-full max-w-lg">
        <Link to="/" className="inline-flex items-center gap-2 text-white/60 hover:text-[#f6e3a8] text-sm mb-6 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Retour au site
        </Link>

        <div className="glass rounded-3xl p-8 md:p-10">
          <div className="flex items-center gap-3 mb-2">
            <span className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#1f6f8b]/50 to-[#5a5ee0]/30 flex items-center justify-center ring-1 ring-white/10">
              <ShieldCheck className="w-5 h-5 text-[#7fd8e8]" />
            </span>
            <div>
              <h1 className="text-2xl text-white">Panneau Admin</h1>
              <p className="text-white/50 text-sm">Gère l'affichage du site</p>
            </div>
          </div>

          <div className="mt-8 space-y-6">
            {/* Page d'accueil */}
            <div>
              <label className="flex items-center gap-2 text-sm text-white/80 mb-2">
                <LayoutTemplate className="w-4 h-4 text-[#7fd8e8]" /> Page affichée sur l'accueil du site
              </label>
              <div className="flex gap-3">
                <VariantBtn value="promo" label="Version Promo" desc="Avec offre, -30% et compte à rebours" />
                <VariantBtn value="classique" label="Version Classique" desc="Sans promo ni urgence" />
              </div>
            </div>

            <div className="h-px bg-white/10" />

            <div>
              <label className="flex items-center gap-2 text-sm text-white/80 mb-2">
                <Clock className="w-4 h-4 text-[#7fd8e8]" /> Date & heure de fin de la promo
              </label>
              <input
                type="datetime-local"
                value={dateVal}
                onChange={(e) => setDateVal(e.target.value)}
                className="w-full rounded-xl bg-[#0a1224] border border-white/15 px-4 py-3 text-white outline-none focus:border-[#7fd8e8] transition-colors [color-scheme:dark]"
              />
              <p className="mt-1.5 text-xs text-white/40">Laisse vide pour revenir au minuteur « 7 jours » automatique par visiteur.</p>
            </div>

            <div className="flex items-center justify-between rounded-xl bg-[#0a1224] border border-white/10 px-4 py-3">
              <div>
                <p className="text-white text-sm font-medium">Compte à rebours actif</p>
                <p className="text-white/45 text-xs">Désactive pour masquer l'urgence</p>
              </div>
              <Switch checked={active} onCheckedChange={setActive} />
            </div>

            <div>
              <label className="block text-sm text-white/80 mb-2">Mot de passe admin</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full rounded-xl bg-[#0a1224] border border-white/15 px-4 py-3 text-white outline-none focus:border-[#7fd8e8] transition-colors"
              />
            </div>

            {status && (
              <div
                className={`flex items-start gap-2 rounded-xl px-4 py-3 text-sm ${
                  status.type === "success"
                    ? "bg-[#27ae8f]/15 text-[#7fe8cf]"
                    : "bg-[#e0464b]/15 text-[#ff9ea1]"
                }`}
              >
                {status.type === "success" ? (
                  <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
                ) : (
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                )}
                <span>{status.msg}</span>
              </div>
            )}

            <button
              onClick={save}
              disabled={loading}
              className="btn-gold rounded-full px-6 py-3.5 font-semibold w-full inline-flex items-center justify-center gap-2 disabled:opacity-60"
            >
              <Save className="w-4 h-4" />
              {loading ? "Enregistrement…" : "Enregistrer"}
            </button>
          </div>
        </div>

        <p className="text-center text-white/35 text-xs mt-5">
          Aperçu direct : <Link to="/promo" className="text-[#7fd8e8]">/promo</Link> ·{" "}
          <Link to="/classique" className="text-[#7fd8e8]">/classique</Link>
        </p>
      </div>
    </div>
  );
}
